import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { BikeIcon, Users, Ruler, Leaf } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

interface OverviewStatsCardsProps {
  isLoading: boolean;
  data?: {
    totalRides: number;
    totalUsers: number;
    totalOffsetRides: number;
    totalDistance: number;
    totalOffsetDistance: number;
    totalCO2Offset: number;
  };
}

const OverviewStatsCards: React.FC<OverviewStatsCardsProps> = ({ isLoading, data }) => {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Rides</p>
              <p className="text-3xl font-bold">
                {isLoading ? <span className="animate-pulse">...</span> : data?.totalRides || 0}
              </p>
            </div>
            <div className="rounded-full bg-blue-100 p-2.5">
              <BikeIcon className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Users</p>
              <p className="text-3xl font-bold">
                {isLoading ? <span className="animate-pulse">...</span> : data?.totalUsers || 0}
              </p>
            </div>
            <div className="rounded-full bg-purple-100 p-2.5">
              <Users className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Offset-Eligible Rides</p>
              <p className="text-3xl font-bold">
                {isLoading ? <span className="animate-pulse">...</span> : data?.totalOffsetRides || 0}
              </p>
            </div>
            <div className="rounded-full bg-green-100 p-2.5">
              <BikeIcon className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Distance</p>
              <p className="text-3xl font-bold">
                {isLoading ? <span className="animate-pulse">...</span> : `${data?.totalDistance || 0} km`}
              </p>
            </div>
            <div className="rounded-full bg-amber-100 p-2.5">
              <Ruler className="h-6 w-6 text-amber-600" />
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Offset-Eligible Distance</p>
              <p className="text-3xl font-bold">
                {isLoading ? <span className="animate-pulse">...</span> : `${data?.totalOffsetDistance || 0} km`}
              </p>
            </div>
            <div className="rounded-full bg-emerald-100 p-2.5">
              <Ruler className="h-6 w-6 text-emerald-600" />
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">CO2 Saved</p>
              <p className="text-3xl font-bold">
                {isLoading ? <span className="animate-pulse">...</span> : `${data?.totalCO2Offset?.toFixed(2) || "0.00"} kg`}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">Based on UK DEFRA conversion factors</p>
            </div>
            <div className="rounded-full bg-green-100 p-2.5">
              <Leaf className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OverviewStatsCards; 